import { useEffect, useState } from "react";
import { FiDownload, FiPhone } from "react-icons/fi";
import Badge from "../common/Badge";
import Button from "../common/Button";
import ReadableSummary from "../common/ReadableSummary";
import RecordingButton from "../calls/RecordingButton";
import {
  downloadTranscript,
  hasTranscript,
} from "../../utils/callDisplay";
import {
  getCallDirectionMeta,
  getCallStatusMeta,
  parseTranscript,
} from "../../utils/helpers";
import {
  formatAppointmentService,
  formatDateTime,
  formatDuration,
  formatPhone,
} from "../../utils/formatters";
import BaseModal from "./BaseModal";

function TranscriptBody({ transcript }) {
  const parsed = parseTranscript(transcript);

  if (parsed.type === "raw") {
    return (
      <pre className="transcript-raw bg-gray-50 p-4 rounded-lg text-[15px] font-mono whitespace-pre-wrap break-words">
        {parsed.text}
      </pre>
    );
  }

  return (
    <div className="transcript-lines space-y-3 max-h-[400px] overflow-y-auto pr-2">
      {parsed.items.map((item, index) => (
        <div
          className="t-line flex gap-3 py-2 border-b border-gray-100 last:border-0"
          key={`${item.speaker}-${index}`}
        >
          <span
            className={`t-speaker ${item.speakerClass} font-semibold min-w-[85px] text-[15px]`.trim()}
          >
            {item.speaker}
          </span>
          <span className="t-text text-gray-800 text-[15px] leading-relaxed flex-1">
            {item.text}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatModalDuration(seconds) {
  const value = Number(seconds || 0);

  if (!Number.isFinite(value) || value <= 0) {
    return "0:00";
  }

  return formatDuration(value);
}

function SummaryField({ label, value, wide = false }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div className={`call-detail-item ${wide ? "wide" : ""}`.trim()}>
      <div className="call-detail-label">
        {label}
      </div>
      <div className="call-detail-value">{value}</div>
    </div>
  );
}

function CallSummarySection({ call }) {
  if (!call) {
    return null;
  }

  return (
    <div className="modal-summary-block call-detail-card">
      <div className="call-detail-grid">
        <SummaryField
          label="Summary"
          value={<ReadableSummary summary={call.summary} />}
          wide
        />
        <SummaryField label="Caller Name" value={call.caller_name || "Unknown"} />
        <SummaryField
          label="Phone"
          value={formatPhone(call.phone_number)}
        />
        <SummaryField
          label="Date & Time"
          value={formatDateTime(call.created_at)}
        />
        <SummaryField
          label="Duration"
          value={formatModalDuration(call.duration_seconds)}
        />
        <SummaryField
          label="Service"
          value={formatAppointmentService(call.service_type)}
        />
      </div>
    </div>
  );
}

function ModalActionBar({
  call,
  transcriptAvailable,
  onDownloadTranscript,
  onCallAgain,
}) {
  return (
    <div className="modal-call-action-bar">
      <RecordingButton
        call={call}
        className="modal-audio-control"
        playLabel="Play Audio"
        hideLabel="Hide Audio"
        downloadLabel="Download Audio"
      />
      <Button
        variant="primary"
        onClick={onDownloadTranscript}
        className="modal-action-btn"
        disabled={!transcriptAvailable}
      >
        <FiDownload />
        <span>Download Transcript</span>
      </Button>
      <Button
        variant="green"
        onClick={onCallAgain}
        className="modal-action-btn"
        disabled={!onCallAgain}
      >
        <FiPhone />
        <span>Call Again</span>
      </Button>
    </div>
  );
}

function CallHistoryRecord({ call }) {
  const callStatus = getCallStatusMeta(call);
  const direction = getCallDirectionMeta(call);
  const transcriptAvailable = hasTranscript(call);

  return (
    <div className="call-history-record">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-gray-900">
            {call.__historyLabel || "Call Record"}
          </div>
          <div className="text-sm text-gray-500">
            {call.caller_name || "Unknown caller"} - {formatPhone(call.phone_number)}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {formatDateTime(call.created_at)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[14px] text-gray-600">
        <Badge variant={callStatus.variant}>
          {callStatus.icon} {callStatus.label}
        </Badge>
        <Badge variant={direction.variant}>
          {direction.icon} {direction.label}
        </Badge>
      </div>

      <CallSummarySection call={call} />

      <div className="space-y-3">
        <div className="transcript-label">
          Transcript
        </div>

        {transcriptAvailable ? (
          <TranscriptBody transcript={call.transcript} />
        ) : (
          <div className="no-transcript text-center py-6 text-gray-400 text-[15px]">
            No transcript available for this call.
          </div>
        )}
      </div>
    </div>
  );
}

function CallHistorySelector({
  calls,
  selectedCallIndex,
  onSelectCall,
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
        Follow-up History
      </div>
      <div className="call-history-selector-list">
        {calls.map((historyCall, index) => {
          const direction = getCallDirectionMeta(historyCall);
          const status = getCallStatusMeta(historyCall);
          const isSelected = index === selectedCallIndex;

          return (
            <div
              key={
                historyCall.__historyKey ||
                historyCall.id ||
                historyCall.call_id ||
                historyCall.created_at
              }
              className={`call-history-selector-card ${
                isSelected
                  ? "selected"
                  : ""
              }`.trim()}
            >
              <button
                type="button"
                onClick={() => onSelectCall(index)}
                className="call-history-selector-main"
              >
                <span className="text-sm font-medium">
                  {index === 0 ? "Call 1 - Latest" : `Call ${index + 1}`}
                </span>
                <span className="text-xs text-slate-500">
                  {formatDateTime(historyCall.created_at)}
                </span>
                <span className="text-xs text-slate-500">
                  {direction.label} - {status.label}
                </span>
              </button>
              <RecordingButton
                call={historyCall}
                compact
                showDownload={false}
                playLabel="Play"
                hideLabel="Hide"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TranscriptModal({
  open,
  call,
  onClose,
  onCallAgain,
  onDownloadTranscript,
}) {
  const safeCall = call || {};
  const historyItems =
    Array.isArray(safeCall.callHistory) && safeCall.callHistory.length
      ? safeCall.callHistory
      : [safeCall];
  const isGroupedHistory = historyItems.length > 1;
  const [selectedCallIndex, setSelectedCallIndex] = useState(0);
  const formattedPhone = formatPhone(safeCall.phone_number);
  const historyTitleValue =
    safeCall.caller_name ||
    (formattedPhone !== "--" ? formattedPhone : "Unknown caller");
  const activeCall = historyItems[selectedCallIndex] || historyItems[0] || safeCall;
  const callStatus = getCallStatusMeta(activeCall);
  const direction = getCallDirectionMeta(activeCall);
  const transcriptAvailable = hasTranscript(activeCall);

  useEffect(() => {
    if (open) {
      setSelectedCallIndex(0);
    }
  }, [open, safeCall.__modalRequestKey]);

  useEffect(() => {
    if (selectedCallIndex >= historyItems.length) {
      setSelectedCallIndex(0);
    }
  }, [historyItems.length, selectedCallIndex]);

  if (!call) {
    return null;
  }

  const handleDownload = () => {
    if (onDownloadTranscript) {
      onDownloadTranscript(activeCall);
      return;
    }

    downloadTranscript(activeCall);
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`Call History - ${historyTitleValue}`}
      size="xl"
      meta={
        <div className="flex flex-wrap items-center gap-2 text-[14px] text-gray-600">
          {formattedPhone} &middot; {historyItems.length} interaction
          {historyItems.length === 1 ? "" : "s"}
          {!isGroupedHistory ? (
            <>
              {" "}&middot; {formatDateTime(call.created_at)} &middot; Duration:{" "}
              {formatModalDuration(call.duration_seconds)}
            </>
          ) : null}
          <Badge variant={callStatus.variant}>
            {callStatus.icon} {callStatus.label}
          </Badge>{" "}
          <Badge variant={direction.variant}>
            {direction.icon} {direction.label}
          </Badge>
        </div>
      }
      modalId="modal"
    >
      <div className="space-y-5">
        <ModalActionBar
          call={activeCall}
          transcriptAvailable={transcriptAvailable}
          onDownloadTranscript={handleDownload}
          onCallAgain={
            onCallAgain
              ? () =>
                  onCallAgain(
                    activeCall.phone_number || "",
                    activeCall.caller_name || "",
                  )
              : null
          }
        />

        {isGroupedHistory ? (
          <div className="space-y-4">
            <CallHistorySelector
              calls={historyItems}
              selectedCallIndex={selectedCallIndex}
              onSelectCall={setSelectedCallIndex}
            />
            <CallHistoryRecord
              call={activeCall}
            />
          </div>
        ) : (
          <>
            <CallSummarySection
              call={call}
            />

            {transcriptAvailable ? (
              <>
                <div className="transcript-label">
                  Full Transcript
                </div>
                <TranscriptBody transcript={call.transcript} />
              </>
            ) : (
              <div className="no-transcript text-center py-8 text-gray-400 text-[15px]">
                No transcript available for this call.
              </div>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
}
